import { ComponentProps, ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { useToast } from '@/components/ui/hooks/use-toast';
import { Select } from '@/components/ui/select';
import { comboBoxOptionsOf, emptySelectOption, requiredSelectOption } from '@/components/ui/multi-select-utils';
import { coachesTypeaheadSearch } from '@/lib/api-call-hooks';
import { createCoachTqReview, getCoachesTqReview } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  CoachTqReviewStatus,
  CoachTqReviewStatusLabelMap,
  CoachTqReviewTypeLabelMap,
  coachTqReviewStatusSchema,
  coachTqReviewTypeSchema,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const createFormSchema = z.object({
  teacher: requiredSelectOption('Please select a teacher'),
  reviewType: coachTqReviewTypeSchema,
  status: coachTqReviewStatusSchema,
});

type CreateForm = z.infer<typeof createFormSchema>;

const renderStatus = (status: CoachTqReviewStatus) =>
  status === CoachTqReviewStatus.blank ? '—' : CoachTqReviewStatusLabelMap[status];

const selectInDialogProps = { modal: false, required: true } as ComponentProps<typeof Select>;

export interface CreateTqReviewProps {
  trigger: ReactNode;
}

export const CreateTqReview = ({ trigger }: CreateTqReviewProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const coachTypeahead = coachesTypeaheadSearch(queryClient);

  const { mutateAsync } = useMutation({ mutationFn: createCoachTqReview });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<CreateForm>) => {
    try {
      await mutateAsync({
        coachId: formData.teacher.value,
        reviewType: formData.reviewType,
        status: formData.status,
      });

      await queryClient.invalidateQueries({ queryKey: getCoachesTqReview().queryKey });

      toast({ description: 'Review created successfully' });
      closeModal();
    } catch (e: unknown) {
      toast({
        description: airServerErrorFrom(e)?.userFacingMessage ?? 'Failed to create review. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const defaultValues: DefaultValues<CreateForm> = {
    teacher: emptySelectOption,
    status: CoachTqReviewStatus.blank,
  };

  return (
    <FormModal
      modalTitle="Add teacher review"
      formSchema={createFormSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={defaultValues}
      confirmBtnLabel="Add"
      cancelBtnLabel="Cancel"
      shouldResetOnCancel
      dialogContentProps={{ className: 'max-w-md lg:max-w-md' }}
    >
      {({ form }) => (
        <div className="flex flex-col gap-4 py-4">
          <FormComboBox
            form={form}
            name="teacher"
            label="Teacher name"
            placeholder="Select a teacher"
            triggerProps={{ className: 'w-full' }}
            required
            {...comboBoxOptionsOf(coachTypeahead)}
          />
          <FormSingleSelect
            name="reviewType"
            label="Review type"
            form={form}
            options={coachTqReviewTypeSchema.options}
            placeholder="Select review type"
            renderOption={(type) => CoachTqReviewTypeLabelMap[type]}
            renderOptionSelected={(type) => (type ? CoachTqReviewTypeLabelMap[type] : 'Select review type')}
            selectProps={selectInDialogProps}
            triggerProps={{ className: 'w-full' }}
          />
          <FormSingleSelect
            name="status"
            label="Review status"
            form={form}
            options={coachTqReviewStatusSchema.options}
            renderOption={renderStatus}
            renderOptionSelected={renderStatus}
            selectProps={selectInDialogProps}
            triggerProps={{ className: 'w-full' }}
          />
        </div>
      )}
    </FormModal>
  );
};
